import clsx from 'clsx';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

// Lib
import { FEEDBACK_EMPTY_STATES } from '@/lib/routes/apiEndpoints';

// Utils
import { post } from '@/utils/client';

// Components
import Loader from '@/components/base/Loader';

type EmptyStateUpVoteProps = {
    emptyStateName: string;
};

export const EmptyStateUpVote = ({ emptyStateName }: EmptyStateUpVoteProps) => {
    const { t } = useTranslation('layout_platform');
    const localStorageUpVoted = localStorage.getItem(`upvoted_${emptyStateName}`);
    const [upVoted, setUpVoted] = useState(localStorageUpVoted === 'true');
    const [isUpVoting, setIsUpVoting] = useState(false);

    const handleUpVote = async () => {
        if (upVoted) return;
        setIsUpVoting(true);
        const response = await post(FEEDBACK_EMPTY_STATES, {
            empty_state_name: emptyStateName,
            feedback: 'I would like to see this feature'
        });
        setIsUpVoting(false);
        if (response?.data?.messageStatus === 'Created') {
            setUpVoted(true);
            localStorage.setItem(`upvoted_${emptyStateName}`, 'true');
        }
    };

    const upVotedEmoji = upVoted ? '✅' : '👍';

    return (
        <div className="flex flex-col items-start gap-2 rounded-2xl bg-icoGray-100 p-8 dark:bg-icoDarkMode-anthracite">
            <h3 className="text-2xl">{t('empty_states.upvote.title')}</h3>
            <p className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                {t('empty_states.upvote.description')}
            </p>
            <div className="flex items-end gap-5">
                <button
                    className={clsx(
                        'rounded-full border-2 bg-white px-7 py-3 dark:bg-icoDarkMode-deepBlack',
                        { 'border-icoGreen': upVoted, 'border-white': !upVoted }
                    )}
                    onClick={handleUpVote}
                    disabled={upVoted || isUpVoting}>
                    {isUpVoting ? (
                        <Loader className="h-5 w-3.5 text-icoGray-400 dark:text-icoDarkMode-wolf" />
                    ) : (
                        upVotedEmoji
                    )}
                </button>
                <Image
                    src="/images/layout/empty-states/arrow-down-left.png"
                    alt="arrow down left"
                    className="mb-5"
                    width={68}
                    height={51}
                />
            </div>
        </div>
    );
};
